import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  BlockStack,
  Button,
  Card,
  Divider,
  InlineStack,
  Spinner,
  Text,
  TextField,
} from '@shopify/polaris';
import { DeleteIcon } from '@shopify/polaris-icons';
import {
  useChatConversation,
  useChatConversations,
  useCreateConversation,
  useDeleteConversation,
  useSendMessage,
} from '../../hooks/useChat';
import type { ChatConversation, ChatMessage } from '../../api/chat';

// ── Message bubble ─────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';

  return (
    <div
      style={{
        display:       'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        marginBottom:  '8px',
      }}
    >
      <div
        style={{
          maxWidth:     '75%',
          padding:      '10px 14px',
          borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
          background:   isUser ? '#005bd3' : '#f1f2f3',
          color:        isUser ? '#fff' : '#1a1a1a',
          fontSize:     '13px',
          lineHeight:   '1.55',
          whiteSpace:   'pre-wrap',
        }}
      >
        {message.content}
        {!isUser && message.cost_usd > 0 && (
          <span style={{ display: 'block', fontSize: '11px', opacity: 0.5, marginTop: '4px' }}>
            ${message.cost_usd.toFixed(5)}
          </span>
        )}
      </div>
    </div>
  );
}

// ── Conversation thread ────────────────────────────────────────────────────

function ConversationThread({
  conversationId,
  onBack,
}: {
  conversationId: number;
  onBack: () => void;
}) {
  const { data, isLoading } = useChatConversation(conversationId);
  const { mutate: send, isPending: isSending } = useSendMessage(conversationId);
  const { mutate: del } = useDeleteConversation();

  const [input, setInput] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);

  const messages = data?.data?.messages ?? [];
  const title    = data?.data?.title ?? 'Conversation';

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text || isSending) return;
    setInput('');
    send(text);
  }, [input, isSending, send]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleDelete = useCallback(() => {
    del(conversationId);
    onBack();
  }, [conversationId, del, onBack]);

  return (
    <BlockStack gap="400">
      <InlineStack gap="200" blockAlign="center" align="space-between">
        <Button variant="plain" onClick={onBack}>← Back</Button>
        <Text as="h2" variant="headingMd" truncate>{title}</Text>
        <Button variant="plain" tone="critical" icon={DeleteIcon} onClick={handleDelete} accessibilityLabel="Delete conversation" />
      </InlineStack>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <Spinner size="small" />
        </div>
      ) : (
        <Card>
          <div style={{ minHeight: '320px', maxHeight: '460px', overflowY: 'auto', padding: '4px 0' }}>
            {messages.length === 0 ? (
              <Text as="p" variant="bodySm" tone="subdued">
                No messages yet. Ask anything about growing your store.
              </Text>
            ) : (
              messages.map((m) => <MessageBubble key={m.id} message={m} />)
            )}
            {isSending && (
              <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
                <div style={{ padding: '10px 14px', background: '#f1f2f3', borderRadius: '18px', opacity: 0.6 }}>
                  <Spinner size="small" />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>
          <Divider />
          <div style={{ marginTop: '12px' }}>
            <InlineStack gap="200" blockAlign="end">
              <div style={{ flex: 1 }} onKeyDown={handleKeyDown}>
                <TextField
                  label=""
                  labelHidden
                  multiline={2}
                  value={input}
                  onChange={setInput}
                  placeholder="Ask about your store… (Enter to send)"
                  autoComplete="off"
                  disabled={isSending}
                />
              </div>
              <Button variant="primary" loading={isSending} disabled={!input.trim()} onClick={handleSend}>
                Send
              </Button>
            </InlineStack>
          </div>
        </Card>
      )}
    </BlockStack>
  );
}

// ── Conversation list ──────────────────────────────────────────────────────

function ConversationList({
  onOpen,
  onCreate,
}: {
  onOpen: (id: number) => void;
  onCreate: () => void;
}) {
  const [page, setPage] = useState(1);
  const { data, isLoading } = useChatConversations(page);

  const conversations = data?.data ?? [];
  const meta          = data?.meta;

  return (
    <BlockStack gap="400">
      <InlineStack gap="200" align="space-between" blockAlign="center">
        <Text as="h2" variant="headingMd">AI Chat</Text>
        <Button variant="primary" size="slim" onClick={onCreate}>New conversation</Button>
      </InlineStack>

      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
          <Spinner size="small" />
        </div>
      ) : conversations.length === 0 ? (
        <Text as="p" variant="bodySm" tone="subdued">
          No conversations yet. Start one to get AI-powered growth advice.
        </Text>
      ) : (
        <BlockStack gap="200">
          {conversations.map((c: ChatConversation) => (
            <Card key={c.id}>
              <InlineStack gap="200" blockAlign="center" align="space-between">
                <BlockStack gap="050">
                  <Button variant="plain" textAlign="left" onClick={() => onOpen(c.id)}>
                    {c.title}
                  </Button>
                  <Text as="p" variant="bodySm" tone="subdued">
                    {c.message_count} messages
                    {c.last_message_at && ` · ${new Date(c.last_message_at).toLocaleDateString()}`}
                  </Text>
                </BlockStack>
                <Badge>{c.message_count > 0 ? 'Active' : 'Empty'}</Badge>
              </InlineStack>
            </Card>
          ))}
        </BlockStack>
      )}

      {meta && meta.last_page > 1 && (
        <InlineStack gap="200" align="center">
          <Button size="slim" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
          <Text as="span" variant="bodySm">Page {meta.current_page} of {meta.last_page}</Text>
          <Button size="slim" disabled={page >= meta.last_page} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </InlineStack>
      )}
    </BlockStack>
  );
}

// ── Public widget ──────────────────────────────────────────────────────────

export function ChatWidget() {
  const [activeId, setActiveId] = useState<number | null>(null);
  const { mutate: create } = useCreateConversation();

  const handleCreate = useCallback(() => {
    create(undefined, {
      onSuccess: (res) => setActiveId(res.data.id),
    });
  }, [create]);

  if (activeId !== null) {
    return <ConversationThread conversationId={activeId} onBack={() => setActiveId(null)} />;
  }

  return (
    <ConversationList
      onOpen={(id) => setActiveId(id)}
      onCreate={handleCreate}
    />
  );
}
